// @ts-ignore
import { createSelector, createAction } from '@reduxjs/toolkit';
import debounceAction from 'debounce-action';

import { apologise } from './apology.thunks';
import { getApiConf } from './subscriptions.thunks';

import { DatasetsAPI } from '../../api/datasets.api';
import { getInternalDataEntities } from '../../utils/commonDataEntities.utils';
import { decorateDataset } from '../../utils/datasets.utils';

import { setInternalDatasets } from '../reducers/internalPipes.reducer';

import type { AppThunk, RootState } from '../../types/state.types';
import type { NodeComponent } from '../../types/common.types';
import type { Dataset } from '../../types/dataset.types';

export const datasetsSelector = createSelector(
  (state: RootState) => state.datasets,
  (datasets) => Object.values(datasets)
);

export const datasetRemoved = createAction<string>('datasets/deleted');
export const datasetLoaded = createAction<{
  id: string;
  dataset: NodeComponent;
}>('datasets/loaded');
export const datasetsLoadAllStarted = createAction('datasets/loadAllStarted');
export const datasetsLoadInternalsStarted = createAction('datasets/loadInternalsStarted');
export const datasetsLoadAllFinished = createAction<NodeComponent[]>('datasets/loadAllFinished');
export const datasetsLoadAllFailed = createAction('datasets/loadAllFailed');
export const datasetsLoadInternalsFailed = createAction('datasets/loadInternalsStartedFailed');
export const datasetRolledBack = createAction<string>('datasets/rolledBack');
export const datasetCommitted = createAction<string>('datasets/committed');
export const datasetsAdded = createAction<Dataset[]>('datasets/added');
export const datasetsUpdated = createAction<Dataset[]>('datasets/updated');
export const datasetsDeleted = createAction<Dataset[]>('datasets/multiple-deleted');

// -----------------------------------------------------------------------------

export const DatasetActions = {
  delete(id: string): AppThunk {
    return function (dispatch, getState) {
      return DatasetsAPI.remove(getApiConf(getState), id)
        .then(() => dispatch(datasetRemoved(id)))
        .catch((error) => dispatch(apologise(error)));
    };
  },

  load(id: string, verbose = true, passthroughError = false): AppThunk {
    return function (dispatch, getState) {
      return DatasetsAPI.get(getApiConf(getState), id, verbose)
        .then((dataset) => {
          dispatch(datasetLoaded({ id, dataset }));
          return dataset;
        })
        .catch((error) => {
          if (!passthroughError) {
            // do nothing
          } else {
            throw error;
          }
        });
    };
  },

  loadAll: debounceAction(
    function (loadInternal = false): AppThunk {
      return function (dispatch, getState) {
        dispatch(datasetsLoadAllStarted());
        return DatasetsAPI.getAll(getApiConf(getState), loadInternal)
          .then((datasets) => dispatch(datasetsLoadAllFinished(datasets)))
          .catch((error) => {
            dispatch(datasetsLoadAllFailed());
            dispatch(apologise(error));
          });
      };
    },
    1000,
    { leading: true, trailing: false }
  ),

  loadAllForced: function (loadInternal = false): AppThunk {
    return function (dispatch, getState) {
      dispatch(datasetsLoadAllStarted());
      return DatasetsAPI.getAll(getApiConf(getState), loadInternal)
        .then((datasets) => dispatch(datasetsLoadAllFinished(datasets)))
        .catch((error) => {
          dispatch(datasetsLoadAllFailed());
          dispatch(apologise(error));
        });
    };
  },

  loadAllInternals: function (): AppThunk {
    return function (dispatch, getState) {
      dispatch(datasetsLoadInternalsStarted());
      return DatasetsAPI.getAll(getApiConf(getState), true)
        .then((allDatasets) => {
          const filtered = getInternalDataEntities<Dataset>(allDatasets).map((d) =>
            decorateDataset(d)
          );

          dispatch(setInternalDatasets(filtered));
        })
        .catch((error) => {
          dispatch(datasetsLoadInternalsFailed());
          dispatch(apologise(error));
        });
    };
  },

  rollback: debounceAction(
    function (id: string): AppThunk {
      return function (dispatch, getState) {
        return DatasetsAPI.rollback(getApiConf(getState), id)
          .then(() => dispatch(datasetRolledBack(id)))
          .catch((error) => dispatch(apologise(error)));
      };
    },
    1000,
    { leading: true, trailing: false }
  ),

  commit: debounceAction(
    function (id: string): AppThunk {
      return function (dispatch, getState) {
        return DatasetsAPI.commit(getApiConf(getState), id)
          .then(() => dispatch(datasetCommitted(id)))
          .catch((error) => dispatch(apologise(error)));
      };
    },
    1000,
    { leading: true, trailing: false }
  ),

  // TODO: IS-6787 This is not really a Redux action; it's dispatched as one so it can
  // access `getState()`. Should rethink this.
  getDatasetIndexes(datasetId: string): AppThunk {
    return function (dispatch, getState) {
      return DatasetsAPI.getIndexes(getApiConf(getState), datasetId).catch((error) => {
        dispatch(apologise(error));
        return [];
      });
    };
  },
};
